import { useEffect, useState } from 'react';

/**
 * SplashAnimation — branded launch screen on cold app start.
 *
 * Per Ernie 2026-05-03 P0.2 (round 2): the v1 was too fast — couldn't even
 * see the animation. Round 2 makes it feel like a deliberate brand moment
 * (think Linear / Spotify / Notion launch sequences) by:
 *   - extending total duration from ~1.4s to ~2.8s
 *   - giving each element more on-screen time after it lands
 *   - bigger icon (h-32 vs h-24) so the brand reads from across the room
 *   - adding a subtle pulse on the icon during the hold so it feels alive
 *
 * Sequence (cold load):
 *   t=0      — full-screen white (or dark bg in dark mode), icon tucked
 *              (scale 0.7, -8° rotation, opacity 0)
 *   t=600ms  — icon springs to scale 1.0, 0° rotation, opacity 1
 *   t=1100ms — wordmark "Service Steady" fades in + slides up beneath
 *   t=1500ms — tagline "Less chaos. More bookings." fades in
 *   t=1500-
 *     2400ms — full lockup HOLDS so the user can actually see it (this is
 *              the bit Ernie called out — splash shouldn't blink past)
 *   t=2400ms — icon adds a gentle pulse to signal we're about to leave
 *   t=2600ms — whole splash crossfades out (350ms)
 *   t=2950ms — component unmounts
 *
 * Reduced motion: skip the choreography, show the static lockup ~1.6s, fade.
 *
 * Once-per-cold-load: module-level flag (not sessionStorage — iOS PWA
 * standalone has flaky session persistence).
 */

let hasShownThisLoad = false;

const TIMINGS = {
  ICON_SETTLED: 600,
  WORDMARK_IN: 1100,
  TAGLINE_IN: 1500,
  PULSE: 2400,
  FADE_OUT_START: 2600,
  FADE_OUT_MS: 350,
} as const;

const REDUCED_MOTION_HOLD_MS = 1600;

export function SplashAnimation(): JSX.Element | null {
  const [visible, setVisible] = useState(() => !hasShownThisLoad);
  // Stage drives the choreography:
  //   0 = initial (icon tucked)
  //   1 = icon settled (wordmark + tagline still hidden)
  //   2 = wordmark in
  //   3 = tagline in (full lockup visible)
  //   4 = pulse (subtle icon scale-up before exit)
  //   5 = fading out
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!visible) return;
    hasShownThisLoad = true;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced) {
      setStage(3); // skip straight to full lockup
      const fadeStart = setTimeout(() => setStage(5), REDUCED_MOTION_HOLD_MS);
      const done = setTimeout(
        () => setVisible(false),
        REDUCED_MOTION_HOLD_MS + TIMINGS.FADE_OUT_MS,
      );
      return () => {
        clearTimeout(fadeStart);
        clearTimeout(done);
      };
    }

    const t1 = setTimeout(() => setStage(1), TIMINGS.ICON_SETTLED);
    const t2 = setTimeout(() => setStage(2), TIMINGS.WORDMARK_IN);
    const t3 = setTimeout(() => setStage(3), TIMINGS.TAGLINE_IN);
    const t4 = setTimeout(() => setStage(4), TIMINGS.PULSE);
    const t5 = setTimeout(() => setStage(5), TIMINGS.FADE_OUT_START);
    const t6 = setTimeout(
      () => setVisible(false),
      TIMINGS.FADE_OUT_START + TIMINGS.FADE_OUT_MS,
    );
    return () => {
      [t1, t2, t3, t4, t5, t6].forEach(clearTimeout);
    };
  }, [visible]);

  if (!visible) return null;

  // Icon scale logic:
  //   stage 0 → 0.7 (tucked, invisible)
  //   stage 1-3 → 1.0 (settled)
  //   stage 4 → 1.05 (gentle pulse before exit)
  const iconScale =
    stage === 0 ? 'scale-75' : stage === 4 ? 'scale-105' : 'scale-100';
  const iconRotate = stage === 0 ? '-rotate-6' : 'rotate-0';
  const iconOpacity = stage === 0 ? 'opacity-0' : 'opacity-100';

  return (
    <div
      aria-hidden="true"
      className={[
        'fixed inset-0 z-50 flex flex-col items-center justify-center',
        'bg-white dark:bg-[#0B1220]',
        'transition-opacity ease-ss-standard',
        stage >= 5 ? 'opacity-0 pointer-events-none' : 'opacity-100',
      ].join(' ')}
      style={{
        transitionDuration: `${TIMINGS.FADE_OUT_MS}ms`,
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* S icon — bigger (h-32 = 128px) so it reads as the brand mark, not
          a small badge. Spring entrance, gentle pulse before exit.
          Rounded corners (rounded-3xl = 24px on 128px ≈ 19%) give it the
          modern app-icon squircle vibe — feels like a real iOS app launching,
          not a stamped logo. */}
      <img
        src="/icons/icon-192.png"
        alt=""
        className={[
          'h-32 w-32 select-none rounded-3xl drop-shadow-md',
          'transition-all duration-700 ease-ss-spring',
          iconScale,
          iconRotate,
          iconOpacity,
        ].join(' ')}
        draggable={false}
      />

      {/* Wordmark — bigger (text-4xl) and tighter tracking for brand impact */}
      <div
        className={[
          'mt-7 text-4xl font-bold tracking-tight text-ss-blue',
          'transition-all duration-500 ease-ss-standard',
          stage >= 2
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-3',
        ].join(' ')}
      >
        Service Steady
      </div>

      {/* Tagline — softer weight, lighter blue, lands last */}
      <div
        className={[
          'mt-2 text-base font-medium text-ss-blue/70',
          'transition-opacity duration-500 ease-ss-standard',
          stage >= 3 ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
      >
        Less chaos. More bookings.
      </div>
    </div>
  );
}
